<template>
  <canvas ref="chartCorrection" id="chartCorrection"></canvas>
</template>

<script>
import Chart from "chart.js";
import moment from "moment";
import { api } from "@/api";
import { mapGetters } from "vuex";
import { round10 } from "round10";
export default {
  name: "ProductCorrectionChart",
  props: {
    take: {
      required: true
    },
    productFilter: {
      required: true
    },
    start: {
      required: true
    },
    end: {
      required: true
    },
    groupBy: {
      required: true
    },
    loading: {
      type: Boolean
    }
  },
  mounted() {
    this.getData();
    this.createChart();
  },
  computed: {
    ...mapGetters(["getInventoryRecords"])
  },
  data() {
    return {
      data: [],
      chart: undefined
    };
  },
  methods: {
    async getData() {
      try {
        this.$emit("update:loading", true);
        this.data = await api.getInventoryRecordsCorrections({
          take: this.take,
          product: this.productFilter,
          omitted: false,
          start:
            this.start != null
              ? moment(this.start)
                  .hours(0)
                  .minutes(0)
                  .format("YYYY-MM-DDTHH:mm:ss.SSSSSSZ")
              : this.start,
          end:
            this.end != null
              ? moment(this.end)
                  .hours(23)
                  .minutes(59)
                  .format("YYYY-MM-DDTHH:mm:ss.SSSSSSZ")
              : this.end
        });
        setTimeout(() => {
          this.updateChart();
        }, 100);
      } finally {
        this.$emit("update:loading", false);
      }
    },
    createChart() {
      const ctx = document.getElementById("chartCorrection");
      this.chart = new Chart(ctx, {
        type: "bar",
        data: {
          datasets: [
            {
              type: "bubble",
              backgroundColor: "#ffa60055",
              borderColor: "#ffa600",
              label: "Correction",
              pointStyle: "triangle",
              fill: false,
              data: this.data.map(x => {
                return {
                  x: x.created,
                  y: Number(x.quantity),
                  r: (Number(Math.abs(x.percent)) + 1) * 7
                };
              })
            },
            {
              type: "line",
              fill: false,
              label: "Correction moyenne (qty)",
              backgroundColor: "#003f5c",
              borderColor: "#003f5c55",
              data: this.data.map(x => {
                const previous_d = this.data.filter(d =>
                  moment(d.created).isBefore(moment(x.created))
                );
                return {
                  x: x.created,
                  y: round10(
                    previous_d
                      .map(d => +d.quantity)
                      .reduce((a, b) => a + b, 0) / previous_d.length,
                    -2
                  )
                };
              })
            },
            {
              type: "line",
              fill: false,
              label: "Correction moyenne (%)",
              backgroundColor: "#a05195",
              borderColor: "#a0519555",
              data: this.data.map(x => {
                const previous_d = this.data.filter(d =>
                  moment(d.created).isBefore(moment(x.created))
                );
                return {
                  x: x.created,
                  y: round10(
                    previous_d
                      .map(d => +d.percent * 100)
                      .reduce((a, b) => a + b, 0) / previous_d.length,
                    -2
                  )
                };
              })
            },
            {
              type: "line",
              fill: false,
              label: "Correction moyenne sans 0 (%)",
              backgroundColor: "#EF9A9A",
              borderColor: "#EF9A9A55",
              data: this.data.map(x => {
                const previous_d = this.data.filter(
                  d =>
                    d.percent !== 0 &&
                    moment(d.created).isBefore(moment(x.created))
                );
                return {
                  x: x.created,
                  y: round10(
                    previous_d
                      .map(d => +d.percent * 100)
                      .reduce((a, b) => a + b, 0) / previous_d.length,
                    -2
                  )
                };
              })
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          aspectRatio: 1.5,
          layout: {
            padding: {
              left: 0,
              right: 0,
              top: 10,
              bottom: 0
            }
          },
          scales: {
            xAxes: [
              {
                type: "time",
                ticks: {
                  maxRotation: 180,
                  min: moment(this.start)
                    .subtract(1, "d")
                    .toDate(),
                  max: moment(this.end)
                    .add(1, "d")
                    .toDate()
                },
                time: {
                  unit: "day",
                  displayFormats: {
                    day: "MM/D"
                  },
                  tooltipFormat: "lll"
                },
                gridLines: {
                  zeroLineColor: "rgba(0,0,0,0.1)",
                  offsetGridLines: false,
                  display: true
                }
              }
            ],
            yAxes: [
              {
                ticks: {
                  display: false
                },
                gridLines: {
                  color: "rgba(0,0,0,0)"
                }
              }
            ]
          }
        }
      });
    },
    updateChart() {
      this.chart.options.scales.xAxes[0].ticks.min = moment(this.start)
        .subtract(1, "d")
        .toDate();
      this.chart.options.scales.xAxes[0].ticks.max = moment(this.end)
        .add(1, "d")
        .toDate();
      this.chart.data.datasets[0].data = this.data.map(x => {
        return {
          x: x.created,
          y: Number(x.quantity),
          r: (Number(Math.abs(x.percent)) + 1) * 7
        };
      });
      this.chart.data.datasets[1].data = this.data.map(x => {
        const previous_d = this.data.filter(d =>
          moment(d.created).isBefore(moment(x.created))
        );
        return {
          x: x.created,
          y: round10(
            previous_d.map(d => +d.quantity).reduce((a, b) => a + b, 0) /
              previous_d.length,
            -2
          )
        };
      });
      this.chart.data.datasets[2].data = this.data.map(x => {
        const previous_d = this.data.filter(d =>
          moment(d.created).isBefore(moment(x.created))
        );
        return {
          x: x.created,
          y: round10(
            previous_d.map(d => +d.percent * 100).reduce((a, b) => a + b, 0) /
              previous_d.length,
            -2
          )
        };
      });
      this.chart.data.datasets[3].data = this.data.map(x => {
        const previous_d = this.data.filter(
          d => d.percent !== 0 && moment(d.created).isBefore(moment(x.created))
        );
        return {
          x: x.created,
          y: round10(
            previous_d.map(d => +d.percent * 100).reduce((a, b) => a + b, 0) /
              previous_d.length,
            -2
          )
        };
      });
      this.chart.update();
    }
  },
  watch: {
    take(v, oldv) {
      if (v !== oldv) {
        this.getData();
      }
    },
    productFilter(v, oldv) {
      if (v !== oldv) {
        this.getData();
      }
    },
    getUncompletedIntegratedTransactionsLength(v, oldv) {
      if (v !== oldv) {
        this.getData();
      }
    },
    start(v, oldv) {
      if (v !== oldv) {
        this.getData();
      }
    },
    end(v, oldv) {
      if (v !== oldv) {
        this.getData();
      }
    },
    getInventoryRecords: {
      deep: true,
      handler: "getData"
    }
  }
};
</script>

<style></style>
